import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Loader2, MessageCircle, Send, User, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Quanto custa um site?",
  "Quero um site para meu jogo",
  "Quanto é 15% de 2.500?",
];

const renderContent = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-zinc-100">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    )
  );

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("lh_chat_session"));
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      fetch(`${API}/chat/session`, { method: "POST", credentials: "include" })
        .then((r) => r.json())
        .then((d) => {
          localStorage.setItem("lh_chat_session", d.session_id);
          setSessionId(d.session_id);
        })
        .catch(() => {});
    }
  }, [sessionId]);

  useEffect(() => {
    if (!open || !sessionId || historyLoaded) return;
    fetch(`${API}/chat/history/${sessionId}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, [open, sessionId, historyLoaded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const friendly =
          res.status === 429
            ? "Calma aí! Você está enviando mensagens muito rápido. Aguarde alguns segundos e tente de novo."
            : typeof data.detail === "string"
              ? data.detail
              : "Não consegui processar sua mensagem agora. Tente novamente.";
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: friendly };
          return copy;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5));
            if (data.delta) {
              acc += data.delta;
              const snapshot = acc;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: snapshot };
                return copy;
              });
            } else if (data.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: data.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Não consegui me conectar agora. Chame o Luiz direto no WhatsApp: +55 31 9633-5635",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-bubble-btn"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[130] flex h-14 w-14 items-center justify-center rounded-full bg-[#E50914] text-white shadow-[0_0_32px_rgba(229,9,20,0.45)]"
        aria-label="Abrir chat de suporte"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E50914] opacity-75" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-[#FF2A3B]" />
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-[130] flex h-[540px] max-h-[calc(100vh-8rem)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[#22222A] bg-[#0A0A0C] shadow-[0_24px_80px_rgba(0,0,0,0.7)] sm:right-6"
          >
            <div className="flex items-center gap-3 border-b border-[#1A1A20] bg-[#101014] px-5 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E50914]">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-display text-sm font-bold text-zinc-100">Assistente LUIZ.DEV</div>
                <div className="flex items-center gap-1.5 font-mono-code text-[10px] uppercase tracking-widest text-zinc-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Online agora
                </div>
              </div>
              <button
                data-testid="chat-close-btn"
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-[#1A1A20] hover:text-white"
                aria-label="Fechar chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="rounded-2xl rounded-tl-sm border border-[#22222A] bg-[#101014] p-4 text-sm leading-relaxed text-zinc-300">
                    Olá! Sou o assistente do Luiz. Posso tirar dúvidas sobre os serviços,
                    explicar como funciona um projeto e até fazer cálculos para você. Como posso ajudar?
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={s}
                        data-testid={`chat-suggestion-${i}`}
                        onClick={() => send(s)}
                        className="rounded-full border border-[#E50914]/40 bg-[#E50914]/10 px-3.5 py-2 text-xs text-[#FF2A3B] transition-colors hover:bg-[#E50914]/25"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E50914]">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[80%] whitespace-pre-wrap rounded-2xl p-3.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-tr-sm bg-[#E50914] text-white"
                        : "rounded-tl-sm border border-[#22222A] bg-[#101014] text-zinc-300"
                    }`}
                  >
                    {m.content === "" && m.role === "assistant" && sending && i === messages.length - 1 ? (
                      <span className="flex gap-1 py-1">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-[#E50914]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-[#E50914] [animation-delay:0.15s]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-[#E50914] [animation-delay:0.3s]" />
                      </span>
                    ) : m.role === "assistant" ? (
                      renderContent(m.content)
                    ) : (
                      m.content
                    )}
                  </div>
                  {m.role === "user" && (
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#2A2A33] bg-[#101014]">
                      <User className="h-4 w-4 text-zinc-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-[#1A1A20] bg-[#101014] p-3.5">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-2"
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Pergunte ou peça um cálculo..."
                  maxLength={1000}
                  className="h-11 flex-1 rounded-full border border-[#22222A] bg-[#050505] px-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
                />
                <button
                  data-testid="chat-send-btn"
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E50914] text-white transition-all hover:shadow-[0_0_20px_rgba(229,9,20,0.5)] disabled:opacity-40 disabled:hover:shadow-none"
                  aria-label="Enviar mensagem"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
